import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Globe, Facebook, MapPin, Star } from "lucide-react";
import { useData } from "@/context/DataContext";
import Logo from "@/components/site/Logo";

const Wa = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.484 8.414c-.003 6.557-5.338 11.892-11.894 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.599 5.393l-.999 3.648 3.9-1.04zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
  </svg>
);

export default function Footer() {
  const { settings, services, zones, localPages } = useData();
  if (!settings) return null;
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="relative bg-av-black text-av-white border-t border-white/10 grain">
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:pr-8">
            <Logo className="h-11" to="/" />
            <p className="mt-5 text-av-gray text-sm max-w-xs">{settings.slogan}</p>
            <div className="mt-6 space-y-3 text-sm">
              <a href={`tel:${settings.telefono_link}`} data-testid="footer-phone" className="flex items-center gap-3 text-av-white/90 hover:text-av-lime transition-colors">
                <Phone size={16} className="text-av-bright" /> {settings.telefono}
              </a>
              <a href={`mailto:${settings.email}`} data-testid="footer-email" className="flex items-center gap-3 text-av-white/90 hover:text-av-lime transition-colors break-all">
                <Mail size={16} className="text-av-bright" /> {settings.email}
              </a>
              <a href={`https://${settings.sito}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-av-white/90 hover:text-av-lime transition-colors">
                <Globe size={16} className="text-av-bright" /> {settings.sito}
              </a>
              <a href={`${settings.whatsapp}?text=${encodeURIComponent(settings.whatsapp_msg)}`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="inline-flex items-center gap-3 text-av-white/90 hover:text-av-lime transition-colors">
                <Wa /> Scrivici su WhatsApp
              </a>
              {settings.facebook && (
                <a href={settings.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook" className="flex items-center gap-3 text-av-white/90 hover:text-av-lime transition-colors">
                  <Facebook size={16} className="text-av-bright" /> Seguici su Facebook
                </a>
              )}
              {settings.google_business && (
                <a href={settings.google_business} target="_blank" rel="noreferrer" data-testid="footer-google" className="flex items-center gap-3 text-av-white/90 hover:text-av-lime transition-colors">
                  <Star size={16} className="text-av-bright" /> Recensioni Google
                </a>
              )}
              {settings.indirizzo && (
                <p className="flex items-start gap-3 text-av-white/70">
                  <MapPin size={16} className="text-av-bright mt-0.5 shrink-0" /> {settings.indirizzo}
                </p>
              )}
            </div>
          </div>

          <FooterCol title="Menu">
            <FooterLink to="/">Home</FooterLink>
            <FooterLink to="/servizi">Servizi</FooterLink>
            <FooterLink to="/chi-siamo">Chi siamo</FooterLink>
            <FooterLink to="/lavori">I nostri lavori</FooterLink>
            <FooterLink to="/galleria">Galleria</FooterLink>
            <FooterLink to="/prima-e-dopo">Prima e dopo</FooterLink>
            <FooterLink to="/zone-servite">Zone servite</FooterLink>
            <FooterLink to="/guide">Guide</FooterLink>
            <FooterLink to="/diagnostica-giardino">Diagnostica giardino</FooterLink>
            <FooterLink to="/domande-frequenti">Domande frequenti</FooterLink>
            <FooterLink to="/blog">Blog</FooterLink>
            <FooterLink to="/contatti">Contatti</FooterLink>
          </FooterCol>

          <FooterCol title="Servizi">
            {(services || []).map((s) => (
              <FooterLink key={s.id} to={`/servizi#${s.slug}`}>{s.titolo}</FooterLink>
            ))}
          </FooterCol>

          <FooterCol title="Zone servite">
            {(zones || []).map((z) => {
              const hasPage = (localPages || []).some((lp) => lp.slug === z.slug);
              return hasPage ? (
                <FooterLink key={z.id} to={`/giardiniere/${z.slug}`}>Giardiniere a {z.nome}</FooterLink>
              ) : (
                <span key={z.id} className="block text-sm text-av-gray">{z.nome}</span>
              );
            })}
          </FooterCol>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-av-gray">
          <p>© {year} {settings.ragione_sociale || "Animaverde"}{settings.partita_iva ? ` · P.IVA ${settings.partita_iva}` : ""}. Tutti i diritti riservati.</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-av-lime">Privacy Policy</Link>
            <Link to="/cookie-policy" data-testid="footer-cookie" className="hover:text-av-lime">Cookie Policy</Link>
            <button
              data-testid="footer-cookie-preferences"
              onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))}
              className="hover:text-av-lime"
            >
              Gestione preferenze cookie
            </button>
          </div>
        </div>
        <p className="mt-4 text-[11px] text-av-gray/60 max-w-3xl">
          Le informative Privacy e Cookie sono modelli generali: si consiglia di farle verificare da un consulente per adeguarle pienamente alla normativa vigente.
        </p>
      </div>
    </footer>
  );
}

const FooterCol = ({ title, children }) => (
  <div>
    <h3 className="overline text-av-bright mb-5">{title}</h3>
    <div className="space-y-2.5">{children}</div>
  </div>
);
const FooterLink = ({ to, children }) => (
  <Link to={to} className="block text-sm text-av-gray hover:text-av-white transition-colors">{children}</Link>
);
